import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Clock, AlertTriangle, User, Phone, MapPin,
  Shield, CreditCard, CheckCircle2, PauseCircle, Play, Wrench,
  ClipboardList, Stethoscope, HardHat, FileCheck, XCircle,
  ChevronRight, RefreshCw,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface Complaint {
  id: string;
  number: string;
  title?: string;
  description?: string;
  status: string;
  sopStage: string;
  priority: string;
  customerName: string;
  customerPhone: string;
  customerAddress?: string;
  invoiceNumber?: string;
  serialNumber?: string;
  productType: string;
  warrantyStatus: string;
  isOnHold: boolean;
  holdReason?: string;
  technicianName?: string;
  technicianPhone?: string;
  technicianArea?: string;
  scheduledAt?: string;
  timeIn?: string;
  timeOut?: string;
  resolution?: string;
  escalationReason?: string;
  escalationLevel?: number;
  escalatedAt?: string;
  preVisitData?: any;
  diagnosisData?: any;
  onSiteData?: any;
  postRepairData?: any;
  warrantyClaimData?: any;
  paidServiceData?: any;
  warrantyCheckData?: any;
  assignedTo?: { id: string; firstName: string; lastName: string; phone?: string };
  warranty?: any;
  contact?: any;
  account?: any;
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
}

const STAGE_ORDER = [
  'intake', 'hold', 'warranty_check', 'paid_service', 'ticket_created',
  'pre_visit', 'diagnosis', 'on_site', 'post_repair', 'closure',
  'warranty_claim', 'escalated', 'closed',
];

const STAGE_LABELS: Record<string, string> = {
  intake: 'Intake',
  hold: 'On Hold',
  warranty_check: 'Warranty Check',
  paid_service: 'Paid Service',
  ticket_created: 'Ticket Created',
  pre_visit: 'Pre-Visit',
  diagnosis: 'Diagnosis',
  on_site: 'On-Site',
  post_repair: 'Post-Repair',
  closure: 'Closure',
  warranty_claim: 'Warranty Claim',
  escalated: 'Escalated',
  closed: 'Closed',
};

const STAGE_ICONS: Record<string, typeof Wrench> = {
  intake: ClipboardList,
  hold: PauseCircle,
  warranty_check: Shield,
  paid_service: CreditCard,
  ticket_created: CheckCircle2,
  pre_visit: ClipboardList,
  diagnosis: Stethoscope,
  on_site: HardHat,
  post_repair: FileCheck,
  closure: CheckCircle2,
  warranty_claim: Shield,
  escalated: AlertTriangle,
  closed: XCircle,
};

const STAGE_COLORS: Record<string, string> = {
  intake: 'bg-gray-100 text-gray-700',
  hold: 'bg-orange-100 text-orange-700',
  warranty_check: 'bg-blue-100 text-blue-700',
  paid_service: 'bg-amber-100 text-amber-700',
  ticket_created: 'bg-indigo-100 text-indigo-700',
  pre_visit: 'bg-purple-100 text-purple-700',
  diagnosis: 'bg-yellow-100 text-yellow-700',
  on_site: 'bg-green-100 text-green-700',
  post_repair: 'bg-teal-100 text-teal-700',
  closure: 'bg-blue-100 text-blue-700',
  warranty_claim: 'bg-pink-100 text-pink-700',
  escalated: 'bg-red-100 text-red-700',
  closed: 'bg-gray-100 text-gray-500',
};

const PRIORITY_COLORS: Record<string, string> = {
  Critical: 'bg-red-100 text-red-700',
  High: 'bg-red-50 text-red-600',
  Medium: 'bg-yellow-50 text-yellow-700',
  Normal: 'bg-gray-100 text-gray-600',
  Low: 'bg-gray-50 text-gray-500',
};

const PRODUCT_LABELS: Record<string, string> = {
  ss_water_tank: 'SS Water Tank',
  heat_pump: 'Heat Pump',
  swh: 'Solar Water Heater',
};

export function ComplaintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComplaint = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/service-sop/complaints/${id}`);
      setComplaint(res.data.data ?? res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load complaint');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComplaint(); }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-[var(--text-muted)]">Loading complaint...</span>
      </div>
    );
  }

  if (error || !complaint) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertTriangle size={36} className="mb-2 text-red-400" />
        <p className="text-sm text-red-600">{error || 'Complaint not found'}</p>
        <button onClick={() => navigate(-1)} className="mt-3 text-sm text-[var(--ink-0)] hover:underline">Go Back</button>
      </div>
    );
  }

  const currentStageIdx = STAGE_ORDER.indexOf(complaint.sopStage);
  const StageIcon = STAGE_ICONS[complaint.sopStage] || Wrench;

  // Determine next action route based on SOP stage
  const getNextActionRoute = () => {
    switch (complaint.sopStage) {
      case 'intake': return null;
      case 'warranty_check': return `/service/warranty-verify/${id}`;
      case 'pre_visit': return `/service/pre-visit/${id}`;
      case 'diagnosis': return `/service/diagnosis/${id}`;
      case 'on_site': return `/service/on-site/${id}`;
      case 'warranty_claim': return `/service/warranty-claim/${id}`;
      default: return null;
    }
  };

  const nextActionRoute = getNextActionRoute();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-lg p-2 hover:bg-[var(--bg-muted)] transition-colors">
            <ArrowLeft size={20} className="text-[var(--text-secondary)]" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">{complaint.number}</h1>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STAGE_COLORS[complaint.sopStage] ?? 'bg-gray-100 text-gray-600'}`}>
                {STAGE_LABELS[complaint.sopStage] ?? complaint.sopStage}
              </span>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[complaint.priority] ?? 'bg-gray-100 text-gray-600'}`}>
                {complaint.priority}
              </span>
              {complaint.isOnHold && (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-700 animate-pulse">
                  <PauseCircle size={11} /> ON HOLD
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              {PRODUCT_LABELS[complaint.productType] ?? complaint.productType} — Created {formatDate(complaint.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchComplaint}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--border-primary)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          {nextActionRoute && (
            <Link
              to={nextActionRoute}
              className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
            >
              <Play size={14} />
              Continue SOP
              <ChevronRight size={14} />
            </Link>
          )}
        </div>
      </div>

      {/* SOP Stage Progress */}
      <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 overflow-x-auto">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">SOP Progress</p>
        <div className="flex items-center gap-1 min-w-max">
          {STAGE_ORDER.filter(s => s !== 'hold').map((stage, idx) => {
            const stageIdx = STAGE_ORDER.indexOf(stage);
            const isCompleted = stageIdx < currentStageIdx;
            const isCurrent = stage === complaint.sopStage;
            const Icon = STAGE_ICONS[stage] || Wrench;
            return (
              <div key={stage} className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                  isCurrent ? 'bg-[var(--saffron-50)] text-[var(--ink-0)] ring-2 ring-[var(--ink-3)]' :
                  isCompleted ? 'bg-green-50 text-green-600' :
                  'bg-gray-50 text-gray-400'
                }`}>
                  <Icon size={11} />
                  {STAGE_LABELS[stage]}
                </div>
                {idx < STAGE_ORDER.filter(s => s !== 'hold').length - 1 && (
                  <ChevronRight size={12} className={isCompleted ? 'text-green-400' : 'text-gray-300'} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Customer Information */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
          <h3 className="mb-4 text-sm font-semibold text-[var(--text-primary)]">Customer Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User size={16} className="text-[var(--text-muted)]" />
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">{complaint.customerName}</p>
                <p className="text-xs text-[var(--text-muted)]">Customer Name</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={16} className="text-[var(--text-muted)]" />
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">{complaint.customerPhone}</p>
                <p className="text-xs text-[var(--text-muted)]">Phone</p>
              </div>
            </div>
            {complaint.customerAddress && (
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[var(--text-muted)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{complaint.customerAddress}</p>
                  <p className="text-xs text-[var(--text-muted)]">Address</p>
                </div>
              </div>
            )}
            {complaint.invoiceNumber && (
              <div className="flex items-center gap-3">
                <FileCheck size={16} className="text-[var(--text-muted)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{complaint.invoiceNumber}</p>
                  <p className="text-xs text-[var(--text-muted)]">Invoice Number</p>
                </div>
              </div>
            )}
            {complaint.serialNumber && (
              <div className="flex items-center gap-3">
                <Shield size={16} className="text-[var(--text-muted)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{complaint.serialNumber}</p>
                  <p className="text-xs text-[var(--text-muted)]">Serial Number</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Technician & Warranty */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
          <h3 className="mb-4 text-sm font-semibold text-[var(--text-primary)]">Assignment & Warranty</h3>
          <div className="space-y-3">
            {(complaint.technicianName || complaint.assignedTo) && (
              <div className="flex items-center gap-3">
                <Wrench size={16} className="text-[var(--text-muted)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {complaint.technicianName || (complaint.assignedTo ? `${complaint.assignedTo.firstName} ${complaint.assignedTo.lastName}` : 'Unassigned')}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">Technician</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Shield size={16} className="text-[var(--text-muted)]" />
              <div>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                  complaint.warrantyStatus === 'in_warranty' ? 'bg-green-100 text-green-700' :
                  complaint.warrantyStatus === 'out_of_warranty' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {complaint.warrantyStatus === 'in_warranty' ? 'In Warranty' :
                   complaint.warrantyStatus === 'out_of_warranty' ? 'Out of Warranty' : 'Pending Verification'}
                </span>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">Warranty Status</p>
              </div>
            </div>
            {complaint.scheduledAt && (
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-[var(--text-muted)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{formatDate(complaint.scheduledAt)}</p>
                  <p className="text-xs text-[var(--text-muted)]">Scheduled Visit</p>
                </div>
              </div>
            )}
            {complaint.holdReason && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="text-xs font-semibold text-orange-700 mb-1">Hold Reason</p>
                <p className="text-sm text-orange-800">{complaint.holdReason}</p>
              </div>
            )}
            {complaint.escalationReason && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-xs font-semibold text-red-700 mb-1">Escalation Reason</p>
                <p className="text-sm text-red-800">{complaint.escalationReason}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SOP Data Sections */}
      <div className="space-y-4">
        {complaint.diagnosisData && (
          <DataSection title="Diagnosis" data={complaint.diagnosisData} />
        )}
        {complaint.preVisitData && (
          <DataSection title="Pre-Visit Checklist" data={complaint.preVisitData} />
        )}
        {complaint.onSiteData && (
          <DataSection title="On-Site Execution" data={complaint.onSiteData} />
        )}
        {complaint.postRepairData && (
          <DataSection title="Post-Repair Verification" data={complaint.postRepairData} />
        )}
        {complaint.paidServiceData && (
          <DataSection title="Paid Service Details" data={complaint.paidServiceData} />
        )}
        {complaint.warrantyClaimData && (
          <DataSection title="Warranty Claim" data={complaint.warrantyClaimData} />
        )}
      </div>

      {/* Resolution */}
      {complaint.resolution && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-5">
          <h3 className="mb-2 text-sm font-semibold text-green-700">Resolution</h3>
          <p className="text-sm text-green-800">{complaint.resolution}</p>
          {complaint.resolvedAt && (
            <p className="mt-2 text-xs text-green-600">Resolved on {formatDate(complaint.resolvedAt)}</p>
          )}
        </div>
      )}
    </div>
  );
}

function DataSection({ title, data }: { title: string; data: any }) {
  if (!data || typeof data !== 'object') return null;
  const entries = Object.entries(data).filter(([_, v]) => v != null && v !== '');

  if (entries.length === 0) return null;

  return (
    <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-5">
      <h3 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map(([key, value]) => (
          <div key={key} className="rounded-lg bg-[var(--bg-muted)] px-3 py-2">
            <p className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
            </p>
            <p className="text-sm text-[var(--text-primary)]">
              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
               Array.isArray(value) ? value.join(', ') :
               typeof value === 'object' ? JSON.stringify(value) :
               String(value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
